import { ChangeEvent, useCallback, useMemo, useState } from "react";
import { clsx } from "clsx";
import { XMarkIcon, CheckIcon } from "@heroicons/react/16/solid";
import * as Headless from "@headlessui/react";
import { truncateMiddle } from "@/util/truncate-middle";
import Tooltip from "@/common/Tooltip";

export interface ComboBoxMultipleOption {
    id: string;
    label: string;
    description?: string | null;
}

export interface ComboBoxMultipleProps {
    options: ComboBoxMultipleOption[];
    value: ComboBoxMultipleOption[];
    onChange: (value: ComboBoxMultipleOption[]) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    immediate?: boolean;
    /**
     * Draws the chosen values as a selection, for a form where they are the
     * answer the reader gave. Grey is for a filter bar, where they are one
     * condition among others.
     */
    accent?: boolean;
}

export function ComboBoxMultiple({
    options,
    value,
    onChange,
    immediate,
    placeholder = "Select options…",
    disabled,
    className,
    accent = false,
}: ComboBoxMultipleProps) {
    const [query, setQuery] = useState("");

    // Pre-compute lowercase labels for faster filtering - this is the expensive operation
    const normalizedOptions = useMemo(
        () =>
            options.map((option) => ({
                ...option,
                normalizedLabel: option.label.toLowerCase(),
            })),
        [options],
    );

    // Use immediate filtering for better responsiveness - no debouncing
    const filteredOptions = useMemo(() => {
        if (query === "") return options;

        const normalizedQuery = query.toLowerCase();
        const startsWithResults: ComboBoxMultipleOption[] = [];
        const containsResults: ComboBoxMultipleOption[] = [];

        // Prioritize startsWith matches for better UX, then includes matches
        for (const option of normalizedOptions) {
            if (option.normalizedLabel.startsWith(normalizedQuery)) {
                startsWithResults.push({
                    id: option.id,
                    label: option.label,
                    description: option.description,
                });
            } else if (option.normalizedLabel.includes(normalizedQuery)) {
                containsResults.push({
                    id: option.id,
                    label: option.label,
                    description: option.description,
                });
            }
        }

        return [...startsWithResults, ...containsResults];
    }, [query, normalizedOptions, options]);

    const handleRemove = useCallback(
        (optionToRemove: ComboBoxMultipleOption) => {
            onChange(value.filter((option) => option.id !== optionToRemove.id));
        },
        [onChange, value],
    );

    const onClose = useCallback(() => setQuery(""), []);

    const onInputChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value;
            // Only update if the value actually changed to prevent unnecessary re-renders
            setQuery((prevQuery) =>
                prevQuery !== newValue ? newValue : prevQuery,
            );
        },
        [],
    );

    // Create a Set for O(1) lookup of selected option IDs
    const selectedIds = useMemo(
        () => new Set(value.map((option) => option.id)),
        [value],
    );

    // Create a Set for O(1) lookup of valid option IDs
    const validOptionIds = useMemo(
        () => new Set(options.map((option) => option.id)),
        [options],
    );

    return (
        <Headless.Combobox
            value={value}
            onChange={onChange}
            onClose={onClose}
            disabled={disabled}
            immediate={immediate}
            multiple
        >
            <div className={clsx("relative", className)}>
                <span
                    data-slot="control"
                    className={clsx([
                        // Basic layout
                        "relative block w-full",
                        // Background color applied to inset pseudo element; no shadow, as on the site
                        "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white",
                        // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
                        "dark:before:hidden",
                        // Focus ring
                        "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-pink-500",
                        // Disabled state
                        "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
                        // Invalid state
                        "has-data-invalid:before:shadow-red-500/10",
                    ])}
                >
                    <div className="relative">
                        <Headless.ComboboxInput
                            displayValue={undefined}
                            onChange={onInputChange}
                            placeholder={placeholder}
                            data-slot="control"
                            className={clsx([
                                className,
                                // Basic layout
                                "relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                                // Horizontal padding
                                "pr-[calc(--spacing(10)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pr-[calc(--spacing(9)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
                                // Typography
                                "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white",
                                // Border
                                "border border-zinc-200 data-hover:border-zinc-300 dark:border-white/10 dark:data-hover:border-white/20",
                                // Background color
                                "bg-transparent dark:bg-white/5",
                                // Hide default focus styles
                                "focus:outline-none",
                                // Invalid state
                                "data-invalid:border-red-500 data-invalid:data-hover:border-red-500 dark:data-invalid:border-red-500 dark:data-invalid:data-hover:border-red-500",
                                // Disabled state
                                "data-disabled:border-zinc-950/20 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/2.5 dark:data-hover:data-disabled:border-white/15",
                                // System icons
                                "dark:scheme-dark",
                            ])}
                        />
                        <Headless.ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2">
                            <svg
                                className="size-5 stroke-zinc-500 group-data-disabled:stroke-zinc-600 group-data-hover:stroke-zinc-700 sm:size-4 dark:stroke-zinc-400 dark:group-data-hover:stroke-zinc-300 forced-colors:stroke-[CanvasText]"
                                viewBox="0 0 16 16"
                                aria-hidden="true"
                                fill="none"
                            >
                                <path
                                    d="M5.75 10.75L8 13L10.25 10.75"
                                    strokeWidth={1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M10.25 5.25L8 3L5.75 5.25"
                                    strokeWidth={1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </Headless.ComboboxButton>
                    </div>
                </span>

                {/* Selected items as chips */}
                {value.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {value.map((option) => {
                            const isInvalid = !validOptionIds.has(option.id);
                            return (
                                <span
                                    key={option.id}
                                    className={clsx(
                                        "inline-flex items-center gap-1 rounded-md px-2 py-1",
                                        "text-sm border",
                                        isInvalid
                                            ? "bg-red-50 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
                                            : accent
                                              ? "bg-zinc-950/5 text-zinc-950 border-zinc-950/10 dark:bg-white/5 dark:text-white dark:border-white/10"
                                              : "bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700",
                                    )}
                                >
                                    <Tooltip
                                        content={
                                            isInvalid
                                                ? `"${option.label}" doesn't exist anymore`
                                                : option.label
                                        }
                                    >
                                        <span className="max-w-50">
                                            {truncateMiddle(option.label)}
                                        </span>
                                    </Tooltip>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleRemove(option);
                                        }}
                                        className={clsx(
                                            "ml-0.5",
                                            isInvalid
                                                ? "hover:text-red-900 dark:hover:text-red-100"
                                                : accent
                                                  ? "hover:text-zinc-700 dark:hover:text-zinc-300"
                                                  : "hover:text-zinc-900 dark:hover:text-zinc-100",
                                        )}
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                <Headless.ComboboxOptions
                    transition
                    anchor="bottom"
                    className={clsx(
                        // Anchor positioning
                        "[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(4)] sm:data-[anchor~=start]:[--anchor-offset:-4px]",
                        // Base styles,
                        "isolate z-50 min-w-[calc(var(--input-width)+8px)] scroll-py-1 rounded-xl p-1 select-none empty:invisible",
                        // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                        "outline outline-transparent focus:outline-none",
                        // Handle scrolling when menu won't fit in viewport
                        "overflow-y-scroll overscroll-contain",
                        // Popover background
                        "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
                        // Shadows
                        "shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset",
                        // Transitions
                        "transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none",
                    )}
                >
                    {filteredOptions.length === 0 && query !== "" ? (
                        <div className="px-4 py-2 text-sm text-zinc-500 dark:text-zinc-400">
                            No options found
                        </div>
                    ) : (
                        filteredOptions.map((option) => {
                            const isSelected = selectedIds.has(option.id);
                            return (
                                <Headless.ComboboxOption
                                    key={option.id}
                                    value={option}
                                    className={clsx(
                                        // Basic layout
                                        "group/option grid w-full cursor-default grid-cols-[1fr_--spacing(5)] items-baseline gap-x-2 rounded-lg py-2.5 pr-2 pl-3.5 sm:grid-cols-[1fr_--spacing(4)] sm:py-1.5 sm:pr-2 sm:pl-3",
                                        // Typography
                                        "text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",
                                        // Focus
                                        "outline-none data-[focus]:bg-zinc-950/5 data-[focus]:text-zinc-950 dark:data-[focus]:bg-white/10 dark:data-[focus]:text-white",
                                        // Forced colors mode
                                        "forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]",
                                        // Disabled
                                        "data-disabled:opacity-50",
                                    )}
                                >
                                    <>
                                        <div className="flex min-w-0 items-center justify-between">
                                            <div>
                                                <div className={"truncate"}>
                                                    {truncateMiddle(
                                                        option.label,
                                                        65,
                                                    )}
                                                </div>
                                                {option.description && (
                                                    <div
                                                        className={
                                                            "text-sm mt-0.5 text-zinc-500 dark:text-zinc-400"
                                                        }
                                                    >
                                                        {option.description}
                                                    </div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <CheckIcon className="relative col-start-2 size-5 self-center stroke-current sm:size-4" />
                                            )}
                                        </div>
                                    </>
                                </Headless.ComboboxOption>
                            );
                        })
                    )}
                </Headless.ComboboxOptions>
            </div>
        </Headless.Combobox>
    );
}
